import type { GameBet, GamePlayer, GameRound, TeamMember } from '@/lib/types'
import { displayName } from '@/lib/format'

/**
 * The parts of Sheep Race that are arithmetic rather than authority.
 *
 * The RULES live in SQL (migrations/0046_games.sql) because the dice have to be
 * rolled somewhere a player cannot reach. What is here is everything the page
 * works out from rows it has already been given: who is on which side, and how
 * many points each person's coins earned. Nothing in this file decides an
 * outcome, which is why it can be checked without a database.
 *
 * `scripts/sheep-race-check.mjs` asserts all of it.
 */

/**
 * The track: boxes MIN_BOX to MAX_BOX, matching the box check in 0055.
 *
 * It starts at 2 because two dice cannot make 1, and the coin bonus only moves
 * a sheep forward — so box 1 was a square nothing could ever land on, and a
 * coin placed there was a coin thrown away.
 */
export const MIN_BOX = 2
export const MAX_BOX = 12
/** Every box on the track, in order. */
export const TRACK = Array.from({ length: MAX_BOX - MIN_BOX + 1 }, (_, i) => MIN_BOX + i)

/**
 * A round, end to end: five seconds of result then twenty-five of betting, so
 * the dice land every thirty (0058). The FIRST round gets fifteen more as a
 * welcome, which is why the opening countdown reads 45 rather than 30.
 */
export const ROUND_SECONDS = 30
export const WELCOME_SECONDS = 15
/** Seconds a round takes bets for, after the result has had its turn. */
export const BETTING_SECONDS = ROUND_SECONDS - 5

/** Seconds the finished round holds the screen. Matches `result_seconds` in 0050. */
export const RESULT_SECONDS = 5

/**
 * Coins a SIDE holds each round, whoever is on it. Matches `coins_per_team` in
 * the limit trigger AND in the roll (0057) — the same number for a side of two
 * and a side of six, so the bigger room does not simply win.
 *
 * Eight rather than four (0057): four over eleven boxes covered a third of the
 * track at most, and the wager was a choice between five positions of which
 * only "none" and "some" changed the outcome. Eight is enough to build a block
 * on the middle of the 2d6 curve AND keep a long shot — so where they go starts
 * to matter as much as how many.
 */
export const COINS_PER_TEAM = 8

/**
 * The most a side may put on the track. The eighth coin is kept back by the
 * rules rather than by anybody remembering to (0058) — a side with nothing in
 * hand has nothing to lose, and every round should be worth playing.
 */
export const MAX_ON_TRACK = 7

/** Coins a side still holds: everything it did not put on the track. */
export function wagerFrom(coinsOnTrack: number): number {
  return Math.max(0, COINS_PER_TEAM - coinsOnTrack)
}

/**
 * What a side scores for a round (0058), and the one piece of arithmetic in
 * this game anybody has to hold in their head:
 *
 *     kept + recovered - (12 - where the sheep finished)
 *
 * Every coin on the track is a coin not kept; every coin the sheep RUNS OVER
 * comes back (0059); and finishing short of the end costs a coin a box. Finish
 * on 12 having placed nothing and you keep all eight.
 *
 * This mirrors `app.roll_sheep_race`, which is the one that counts. It is here
 * so the page can show the sum rather than just the total, and so the tutorial
 * and `scripts/sheep-race-check.mjs` work from the same definition.
 */
export function roundScore({
  placed,
  recovered,
  position,
}: {
  placed: number
  recovered: number
  position: number
}): number {
  return COINS_PER_TEAM - placed + recovered - (MAX_BOX - position)
}

/**
 * Which of a side's coins the sheep ran over, given how far the DICE reached.
 *
 * Collection is measured against the dice (0059), not against where the sheep
 * ends up, because each collected coin pushes it a box further and a push that
 * collected again would cascade: a row of low coins would then be picked up
 * almost every round, and the opening would stop being a decision.
 */
export function coinsCollected(coins: number[], reach: number): number[] {
  return coins.filter((box) => box <= reach)
}

/** Where the sheep stops: the dice, plus a box per coin collected, capped. */
export function landingFrom(reach: number, collected: number): number {
  return Math.min(reach + collected, MAX_BOX)
}

/**
 * Rolls per round: two dice, thrown once. Matches `rolls_per_round` in
 * app.roll_sheep_race, which 0047 took back down to one.
 */
export const ROLLS_PER_ROUND = 1

/**
 * A coin that the sheep landed on is worth this to the person who placed it.
 *
 * The personal board and the ROUND are scored differently on purpose. A round
 * is coins, counted in SQL (0049), where every coin is worth exactly one — the
 * team's own standing coin included. This is the individual bragging line, and
 * a correct call is worth more because it is the harder bet and because the
 * player who makes it has given up moving their own sheep to do it.
 */
export const POINTS_PER_COIN = 1

/**
 * Calling the round correctly is worth this.
 *
 * Unused since 0053: the wager belongs to the SIDE and is whatever it did not
 * spend, so there is no longer a person to credit it to. Kept because the
 * scoreboard still handles a `win_bet` row from a round played before then.
 */
export const POINTS_PER_WIN_CALL = 2

/** Seconds remaining before betting closes, floored at zero. */
export function secondsLeft(closesAt: string | null | undefined, now: number): number {
  if (!closesAt) return 0
  return Math.max(0, Math.ceil((new Date(closesAt).getTime() - now) / 1000))
}

/** Which side a person is on, or null when they are not playing. */
export function sideOf(
  players: Pick<GamePlayer, 'user_id' | 'side'>[],
  userId: string | null | undefined,
): 'a' | 'b' | null {
  if (!userId) return null
  return players.find((p) => p.user_id === userId)?.side ?? null
}

export interface ScoreRow {
  userId: string
  name: string
  side: 'a' | 'b'
  /** Coins this person placed that their sheep actually landed on. */
  hits: number
  /** Rounds they called correctly. */
  calls: number
  points: number
}

/**
 * Individual points, for the sidebar.
 *
 * Separate from the match score on purpose: the team wins rounds, and this is
 * the bragging. A coin counts when the sheep ENDED a roll on that box at any
 * point in the round — the bonus is paid on landing, so the landing is what the
 * player predicted, not the final position.
 *
 * A bet is a coin OR a call (0048), so the two branches below are exclusive in
 * practice. They are both still here because the scoreboard reads rows, and a
 * row that somehow carried both should score both rather than quietly pick one.
 *
 * Only rounds that have been played are counted. A round still taking bets
 * scores nothing, which is what stops the board flickering with points for a
 * guess nobody has tested yet.
 */
export function scoreboard(
  players: Pick<GamePlayer, 'user_id' | 'side'>[],
  bets: Pick<GameBet, 'user_id' | 'round_no' | 'attempt' | 'box' | 'win_bet'>[],
  rounds: Pick<GameRound, 'round_no' | 'attempt' | 'rolls' | 'lead'>[],
  roster: TeamMember[],
): ScoreRow[] {
  const rows: ScoreRow[] = players.map((p) => ({
    userId: p.user_id,
    // displayName already answers "Unknown" for a row it cannot find, which is
    // what the rest of the app shows. A second fallback here would only invent
    // a different word for the same person.
    name: displayName(roster.find((m) => m.user_id === p.user_id) ?? null),
    side: p.side,
    hits: 0,
    calls: 0,
    points: 0,
  }))
  const byUser = new Map(rows.map((r) => [r.userId, r]))

  for (const round of rounds) {
    // A round with no winner yet has not finished, and its rolls so far can
    // still be scored — a coin the sheep has already landed on is earned.
    const landings = { a: new Set<number>(), b: new Set<number>() }
    for (const roll of round.rolls ?? []) {
      landings.a.add(roll.pos_a)
      landings.b.add(roll.pos_b)
    }
    if (landings.a.size === 0 && landings.b.size === 0) continue

    // Matched on the ATTEMPT too (0055): a replayed round has coins from both
    // goes on the record, and a coin from the void attempt did not earn
    // anything in the one that counted.
    for (const bet of bets.filter(
      (b) => b.round_no === round.round_no && (b.attempt ?? 1) === (round.attempt ?? 1),
    )) {
      const row = byUser.get(bet.user_id)
      if (!row) continue
      if (bet.box != null && landings[row.side].has(bet.box)) {
        row.hits += 1
        row.points += POINTS_PER_COIN
      }
      // Scored against the LEAD — which sheep got furthest — not against the
      // round, which is now decided on coins and can go the other way (0049).
      if (bet.win_bet && round.lead && round.lead === row.side) {
        row.calls += 1
        row.points += POINTS_PER_WIN_CALL
      }
    }
  }

  // Highest first, then by name so the order is stable while points are level.
  return rows.sort((x, y) => y.points - x.points || x.name.localeCompare(y.name))
}

/**
 * How the sides come out when a race starts.
 *
 * A mirror of the allocation in app.start_game_session — ordered by user id,
 * alternating — kept here so the page can show a roster before the RPC returns,
 * and so the rule is checkable. The database is still the one that allocates;
 * if these two ever disagree, this one is wrong.
 */
export function allocateSides(userIds: string[]): Array<{ userId: string; side: 'a' | 'b' }> {
  return [...userIds]
    .sort()
    .map((userId, i) => ({ userId, side: i % 2 === 0 ? ('a' as const) : ('b' as const) }))
}
